/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { gql, useMutation } from '@apollo/client';
import { Bool, Nix, Num, Obj, Str } from '@livecontrol/core-utils';
import type { SchedulingDefaults } from '@livecontrol/scheduler/model';
import { useCallback, useState } from 'react';
import type { MutationResult } from '../../graphql';
import * as Dbx from './dbx';


interface TVariables {
    input: {
        client_id: number;
        location_id?: string;
        settings: {
            recurring?: Dbx.SchedulingDefaultsSetting;
            on_site_contact?: Dbx.SchedulingDefaultsSetting;
            custom_background?: Dbx.SchedulingDefaultsSetting;
            password_protect?: Dbx.SchedulingDefaultsSetting;
            password?: Dbx.SchedulingDefaultsSetting<string>;
            live_chat?: Dbx.SchedulingDefaultsSetting;
            event_registration?: Dbx.SchedulingDefaultsSetting;
            require_registration?: Dbx.SchedulingDefaultsSetting;
            guestbook?: Dbx.SchedulingDefaultsSetting;
            slides?: Dbx.SchedulingDefaultsSetting;
            show_viewers?: Dbx.SchedulingDefaultsSetting;
            join_zoom_event?: Dbx.SchedulingDefaultsSetting;
            cue_sheet?: Dbx.SchedulingDefaultsSetting<string>;
            iso_recordings?: Dbx.SchedulingDefaultsSetting;
            redirect_viewers?: Dbx.SchedulingDefaultsSetting<string>;
            video_notes?: Dbx.SchedulingDefaultsSetting<string>;
            destinations?: Dbx.SchedulingDefaultsSetting<string[]>;
        };
    };
}

interface TData {
    upsertSchedulingDefaults?: Dbx.Record;
}

interface Args {
    accountId?: number;
    locationId?: string;
    settings?: Partial<SchedulingDefaults['settings']>;
    settingsFavored?: Partial<SchedulingDefaults['settingsFavored']>;
}

const MUTATION = gql`
mutation UpsertSchedulingDefaults($input: UpsertClientSchedulingDefaultsInput!) {
  upsertSchedulingDefaults(input: $input) {
    id
    default_slides {
      id
      slide_type
      file_name
      file_type
      url
      duration
      size
      position_index
      created_at
      updated_at
    }
    settings {
      password_protect {
        enabled
        favored
      }
      password {
        enabled
        favored
      }
      live_chat {
        enabled
        favored
      }
      event_registration {
        enabled
        favored
      }
      require_registration {
        enabled
        favored
      }
      guestbook {
        enabled
        favored
      }
      slides {
        enabled
        favored
      }
      show_viewers {
        enabled
        favored
      }
      join_zoom_event {
        enabled
        favored
      }
      iso_recordings {
        enabled
        favored
      }
      cue_sheet {
        enabled
        favored
      }
      redirect_viewers {
        enabled
        favored
      }
      video_notes {
        enabled
        favored
      }
      destinations {
        enabled
        favored
      }
    }
    user {
      id
    }
    location {
        id
    }
  }
}
`;

export const useUpsertEventDefaults = (): [
    (args: Args) => Promise<SchedulingDefaults | undefined>,
    MutationResult<SchedulingDefaults, 'defaults'>
] => {
    const [defaults, setDefaults] = useState<SchedulingDefaults>();
    const [mutation, { loading, error, called }] = useMutation<TData, TVariables>(MUTATION);

    return [
        useCallback(async (args: Args) => {
            const required = {
                client_id: Num.normalize(args.accountId)
            };

            if (!Obj.isHydrated(required)) {
                return undefined;
            }

            const input: TVariables['input'] = {
                location_id: Str.normalize(args.locationId),
                client_id: required.client_id,
                settings: {}
            };

            if (Nix.isNotNil(args.settings?.password) || Nix.isNotNil(args.settingsFavored?.password)) {
                input.settings.password = {
                    favored: args.settingsFavored?.password !== undefined ? Bool.normalize(args.settingsFavored?.password) : undefined,
                    enabled: Str.normalize(args.settings?.password) ?? ''
                };
            }

            if (Nix.isNotNil(args.settings?.liveChat)) {
                input.settings.live_chat = {
                    enabled: Bool.normalize(args.settings?.liveChat),
                    favored: args.settingsFavored?.liveChat !== undefined ? Bool.normalize(args.settingsFavored?.liveChat) : undefined
                };
            }

            if (Nix.isNotNil(args.settings?.eventRegistration)) {
                input.settings.event_registration = {
                    enabled: Bool.normalize(args.settings?.eventRegistration),
                    favored: args.settingsFavored?.eventRegistration !== undefined ? Bool.normalize(args.settingsFavored?.eventRegistration) : undefined
                };
            }

            if (Nix.isNotNil(args.settings?.requireRegistration)) {
                input.settings.require_registration = {
                    enabled: Bool.normalize(args.settings?.requireRegistration),
                    favored: args.settingsFavored?.requireRegistration !== undefined ? Bool.normalize(args.settingsFavored?.requireRegistration) : undefined
                };
            }

            if (Nix.isNotNil(args.settings?.customBackground)) {
                input.settings.custom_background = {
                    enabled: false,
                    favored: args.settingsFavored?.customBackground !== undefined ? Bool.normalize(args.settingsFavored?.customBackground) : undefined
                };
            }

            if (Nix.isNotNil(args.settings?.recurring)) {
                input.settings.recurring = {
                    enabled: false,
                    favored: args.settingsFavored?.recurring !== undefined ? Bool.normalize(args.settingsFavored?.recurring) : undefined
                };
            }

            if (Nix.isNotNil(args.settings?.onSiteContact)) {
                input.settings.on_site_contact = {
                    enabled: false,
                    favored: args.settingsFavored?.onSiteContact !== undefined ? Bool.normalize(args.settingsFavored?.onSiteContact) : undefined
                };
            }

            if (Nix.isNotNil(args.settings?.guestbook)) {
                input.settings.guestbook = {
                    enabled: Bool.normalize(args.settings?.guestbook),
                    favored: args.settingsFavored?.guestbook !== undefined ? Bool.normalize(args.settingsFavored?.guestbook) : undefined
                };
            }

            if (Nix.isNotNil(args.settings?.slides)) {
                input.settings.slides = {
                    enabled: Bool.normalize(args.settings?.slides),
                    favored: args.settingsFavored?.slides !== undefined ? Bool.normalize(args.settingsFavored?.slides) : undefined
                };
            }

            if (Nix.isNotNil(args.settings?.showViewers)) {
                input.settings.show_viewers = {
                    enabled: Bool.normalize(args.settings?.showViewers),
                    favored: args.settingsFavored?.showViewers !== undefined ? Bool.normalize(args.settingsFavored?.showViewers) : undefined
                };
            }

            if (Nix.isNotNil(args.settings?.joinZoomEvent)) {
                input.settings.join_zoom_event = {
                    enabled: Bool.normalize(args.settings?.joinZoomEvent),
                    favored: args.settingsFavored?.joinZoomEvent !== undefined ? Bool.normalize(args.settingsFavored?.joinZoomEvent) : undefined
                };
            }

            if (Nix.isNotNil(args.settings?.cueSheetUrl)) {
                input.settings.cue_sheet = {
                    enabled: Str.normalize(args.settings?.cueSheetUrl) ?? '',
                    favored: args.settingsFavored?.cueSheetUrl !== undefined ? Bool.normalize(args.settingsFavored?.cueSheetUrl) : undefined
                };
            }

            if (Nix.isNotNil(args.settings?.iso)) {
                input.settings.iso_recordings = {
                    enabled: Bool.normalize(args.settings?.iso),
                    favored: args.settingsFavored?.iso !== undefined ? Bool.normalize(args.settingsFavored?.iso) : undefined
                };
            }

            if (Nix.isNotNil(args.settings?.redirectUrl)) {
                input.settings.redirect_viewers = {
                    enabled: Str.normalize(args.settings?.redirectUrl) ?? '',
                    favored: args.settingsFavored?.redirectUrl !== undefined ? Bool.normalize(args.settingsFavored?.redirectUrl) : undefined
                };
            }

            if (Nix.isNotNil(args.settings?.videoNotes)) {
                input.settings.video_notes = {
                    enabled: Str.normalize(args.settings?.videoNotes) ?? '',
                    favored: args.settingsFavored?.videoNotes !== undefined ? Bool.normalize(args.settingsFavored?.videoNotes) : undefined
                };
            }

            if (Nix.isNotNil(args.settings?.destinations)) {
                input.settings.destinations = {
                    enabled: Array.isArray(args?.settings?.destinations) ? args?.settings?.destinations : [],
                    favored: Bool.normalize(args.settingsFavored?.destinations) ?? false
                };
            }

            let defaults_: SchedulingDefaults | undefined;

            try {
                if (Object.keys(input.settings).length) {
                    const response = await mutation({
                        variables: { input },
                        update: (cache, result) => {
                            const variables = { clientId: required.client_id };
                            const q = cache.readQuery<{ getSchedulingDefaultsByClientId: { id: number }[] }>({ query: Dbx.QUERY, variables });

                            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
                            const list = (() => {
                                const settings = q?.getSchedulingDefaultsByClientId;
                                const upsertSetting = result.data?.upsertSchedulingDefaults;

                                if (!upsertSetting?.location) {
                                    return [upsertSetting];
                                }

                                return settings?.some(setting => setting.id === upsertSetting.id )
                                ? settings.map(setting => setting.id !== upsertSetting.id ? setting : upsertSetting)
                                : [...(settings ?? []), upsertSetting];
                                })();

                            cache.writeQuery({ query: Dbx.QUERY, variables, data: {
                                getSchedulingDefaultsByClientId: list
                            } });
                    }});

                    defaults_ = Dbx.normalize(response.data?.upsertSchedulingDefaults);
                }
            } catch {
                //
            }

            setDefaults(defaults_);

            return defaults_;
        }, [mutation]),
        {
            defaults,
            loading,
            error,
            called
        }
    ];
};
